import * as React from 'react'
import { ModalWindow } from '$views/components/ModalWindow'
import { FormContainer } from '$views/components/FormContainer'
import { theme, styled } from '$utils/theme'
import { LoginWindow } from './components/LoginWindow'
import Layout from '$views/components/Layout'
import { ErrorView } from '$views/components/Error/Error.model'
import { verifyToken } from '$services/session/verification/verification.service'
import { formatError } from '$views/components/Error/Error.mapper'
import { userGroupService } from '$utils/services/userGroupService'
import { routeProvider } from '$utils/routeProvider'
import { isUserAuthenticated } from '$services/session/isAuthenticated/isAuthenticated.service'
import history from '$utils/history'
import { redirectUserPath } from '$infrastructure/redirectUserPath/redirectUserPath'
import { getUserIdData } from '$services/user/userIdData/userIdData.service'
import { localUserIdData } from '$utils/services/saveUserIdDataService/saveUserIdDataService'
import { MediaQueryOwn } from '$views/components/MediaQueryOwn'
import { loginUser } from '$services/session/login/login.service'

interface LoginFormValues {
  email: string
  password: string
}

interface WindowProps {
  onSubmit: (loginCuidador: LoginFormValues) => void
  isLoading: boolean
  notification?: string
  showNotification: boolean
  onNotificationClick: () => void
  errorNotification?: ErrorView
  showErrorNotification: boolean
  onErrorNotificationClick: () => void
  fluid?: boolean
  goBack: () => void
}

interface Props {
  token?: string
}

const FadeLogin = styled.div`
  animation: 0.7s ${p => p.theme.animations.fadeIn};
`

const DesktopLoginView: React.SFC<WindowProps> = props => (
  <FormContainer login>
    <LoginWindow {...props} />
  </FormContainer>
)

const MobileLoginView: React.SFC<WindowProps> = ({ goBack, fluid, ...props }) => (
  <FadeLogin>
    <ModalWindow hasLogo onClose={goBack}>
      <LoginWindow {...props} fluid={true} />
    </ModalWindow>
  </FadeLogin>
)

const externalApplicationPath = 'http'

export const Login: React.FC<Props> = ({ token }) => {
  const [isLoading, handleLoading] = React.useState<boolean>(false)
  const [errorNotification, setErrorNotification] = React.useState<ErrorView>(undefined)
  const [showErrorNotification, setShowErrorNotification] = React.useState<boolean>(false)
  const [notification, setNotification] = React.useState<string>(undefined)
  const [showNotification, setShowNotification] = React.useState<boolean>(false)

  const changeNotificationText = newNotification => {
    setNotification(newNotification)
    setShowNotification(true)
  }

  const verifyUserAccount = async (userToken: string) => {
    handleLoading(true)
    try {
      await verifyToken(userToken)
      changeNotificationText('Usuario activado correctamente')
      handleLoading(false)
    } catch (error) {
      const verifyError = formatError(error)
      setErrorNotification(verifyError)
      setShowErrorNotification(true)
      handleLoading(false)
    }
  }

  const redirectToPrivateHome = () => {
    if (userGroupService.isCaretaker()) {
      history.push(routeProvider.offersForCaretaker)
    } else if (userGroupService.isNormalCustomer()) {
      history.push(routeProvider.myUserArea)
    } else if (userGroupService.isAdmin()) {
      window.location.assign(routeProvider.clientMyServices)
    } else {
      history.push(routeProvider.myServices)
    }
  }

  React.useEffect(() => {
    if (token) {
      verifyUserAccount(token)
    }
    if (isUserAuthenticated()) {
      redirectToPrivateHome()
    }
  }, [])

  const urlIsExternal = redirectPath => redirectPath.indexOf(externalApplicationPath) > -1

  const redirectUser = profile => {
    const redirectPath = redirectUserPath(profile)
    if (urlIsExternal(redirectPath)) {
      window.location.assign(redirectPath)
    }
    if (redirectPath && !urlIsExternal(redirectPath)) {
      history.push(redirectPath)
    }
  }

  const onSubmit = async loginInfo => {
    handleLoading(true)
    try {
      const profile = await loginUser(loginInfo)
      const userIdData = await getUserIdData()
      localUserIdData.setUserIdData(userIdData)
      redirectUser(profile)

      handleLoading(false)
    } catch (error) {
      const loginError = formatError(error)
      setErrorNotification(loginError)
      setShowErrorNotification(true)
      handleLoading(false)
    }
  }

  return (
    <Layout>
      <MediaQueryOwn minWidth={theme.breakpoints.desktop}>
        <DesktopLoginView
          isLoading={isLoading}
          errorNotification={errorNotification}
          showErrorNotification={showErrorNotification}
          showNotification={showNotification}
          onSubmit={onSubmit}
          notification={notification}
          onErrorNotificationClick={() => setShowErrorNotification(false)}
          onNotificationClick={() => setShowNotification(false)}
          goBack={() => window.history.back()}
        />
      </MediaQueryOwn>
      <MediaQueryOwn maxWidth={theme.breakpoints.desktop - 1}>
        <MobileLoginView
          isLoading={isLoading}
          errorNotification={errorNotification}
          showErrorNotification={showErrorNotification}
          showNotification={showNotification}
          onSubmit={onSubmit}
          notification={notification}
          onErrorNotificationClick={() => setShowErrorNotification(false)}
          onNotificationClick={() => setShowNotification(false)}
          goBack={() => window.history.back()}
        />
      </MediaQueryOwn>
    </Layout>
  )
}



// WEBPACK FOOTER //
// ./src/views/Login/Login.tsx